"use client";
import { Suspense, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { Box, LinearProgress } from "@mui/material";
import Bytez from "bytez.js";

import { observer, useStore } from "../../../service/mobx";
import { firestore } from "../../../service/firebase/firestore";
import Tabs from "../Tabs";

const Play = dynamic(() => import("./Play"));
const Api = dynamic(() => import("./Api"));
const Related = dynamic(() => import("./Related"));
const Details = dynamic(() => import("./Details"));

function Content() {
  const cluster = useModelListeners();
  const tab = usePathname().split("/").at(-1);
  const Component =
    {
      play: Play,
      api: Api,
      related: Related,
      details: Details
    }[tab] ?? (() => {});

  return (
    <Box m={{ compact: -2, expanded: -3 }} mt={{ compact: 2, expanded: 4 }}>
      <Tabs tab={tab} />
      <Suspense
        fallback={
          <Box bgcolor="var(--surface-container-low)" minHeight="66vh">
            <LinearProgress />
          </Box>
        }
      >
        <Box
          minHeight="66vh"
          p={{ compact: 2, expanded: 3 }}
          bgcolor="var(--surface-container-low)"
        >
          <Component cluster={cluster} />
        </Box>
      </Suspense>
    </Box>
  );
}

export default observer(Content);

const INSTANCES_COLLECTION = "models-api";
const CONTAINERS_COLLECTION = "containers";
// const INSTANCES_COLLECTION = "models-api-test";
// const CONTAINERS_COLLECTION = "containers-test";

export function useModelListeners() {
  const [cluster, setCluster] = useState();
  const { user, model } = useStore();

  useEffect(() => {
    if (model.node.modelId && model.allowed && !model.tier.enterprise) {
      // let mounted = true;
      const cluster = new Bytez(
        user.key,
        process.env.NODE_ENV === "development"
      ).model(model.node.modelId);

      const status = { instance: false, ready: false, request: false };

      const updateStatus = () => {
        model.set.clusterDoc({
          multiTenant: true,
          status: status.ready
            ? "ready"
            : status.instance && status.request
            ? "downloading weights"
            : status.request
            ? "booting instance"
            : "off"
        });
      };

      setCluster(cluster);

      const subscriptions = [
        firestore
          .collection(INSTANCES_COLLECTION)
          .query({
            limit: 1,
            where: [
              // NOTE if cloudIdentifier is not bytez, that means it's a customer on their cloud's container, and is subsequently blocked
              // by firestore rules. We must specify the container is a "bytez" container in order for this to work
              ["cloudIdentifier", "==", "bytez"],
              ["cloudProvider", "==", "lambda-cloud"],
              ["available", ">", model.node.size]
            ]
          })
          .watch(({ docs }) => {
            status.instance = docs.length > 0;

            updateStatus();
          }),
        firestore
          .collection(INSTANCES_COLLECTION)
          .query({
            limit: 1,
            where: [
              ["cloudIdentifier", "==", "bytez"],
              ["cloudProvider", "==", "lambda-cloud"],
              ["pendingModelIds", "array-contains", model.node.modelId]
            ]
          })
          .watch(({ docs }) => {
            status.request = docs.length > 0;

            updateStatus();
          }),
        firestore
          .collectionGroup(CONTAINERS_COLLECTION)
          .query({
            limit: 1,
            where: [
              ["cloudIdentifier", "==", "bytez"],
              ["cloudProvider", "==", "lambda-cloud"],
              ["modelId", "==", model.node.modelId]
            ]
          })
          .watch(({ docs }) => {
            status.ready = docs.length > 0;

            updateStatus();
          })
      ];

      return () => {
        model.set.clusterDoc();
        subscriptions.forEach(unsubscribe => unsubscribe());
      };
    }
  }, [model, user, model.node.modelId, user.key]);

  return cluster;
}
